import React, { useState, useEffect } from 'react';
import SlideoutLayout from '../../layout/slideout/SlideoutLayout';
import { toast } from 'react-toastify';
import GameForm from '../components/GameForm';
import { useForm } from 'react-hook-form';
import permissionTypes from '../../../constants/permissionTypes';
import { getCompanyIdsForPermission } from '../../auth/authUtils';
import { useTranslation } from 'react-i18next';
import Button from '../../common/Button';
import { fetchCompanies } from '../../../reducers/companyReducer';
import { fetchGameFranchises } from '../../../reducers/gameReducer';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { saveGame } from '../../../reducers/gameReducer';
import { getGameById } from '../../../api/gameApi';

export interface EditGameProps {
  done: (savedGame?: any) => void;
  id: string | undefined;
  cancel: () => void;
  addGameFranchise: () => void;
  registerOnFranchiseCreated?: (callback: ((franchise: any) => void) | undefined) => void;
}

function EditGame({ done, id, cancel, addGameFranchise, registerOnFranchiseCreated }: EditGameProps) {
  const dispatch = useAppDispatch();
  const gameFranchises = useAppSelector((state) => state.gameFranchises);
  const companies = useAppSelector((state) => state.companies);
  const currentUser = useAppSelector((state) => state.auth);

  const { t } = useTranslation();
  const {
    register,
    formState: { errors },
    handleSubmit,
    control,
    setValue,
  } = useForm();


  //gameFranchises
  useEffect(() => {
    if (!gameFranchises) {
      dispatch(fetchGameFranchises());
    }
  }, [gameFranchises, dispatch]);

  //companies
  useEffect(() => {
    if (!companies) {
      dispatch(fetchCompanies());
    }
  }, [companies, dispatch]);

  const [game, setGame] = useState<any>();

  const onSubmit = (data: any) => {
    if (!game) {
      toast.error(t('Failed to save game'));
      return;
    }

    let gameRecord: any = {
      id: id,
      isArchived: game.isArchived,
      name: data.name,
      appId: data.appId,
      licenseExpires: data.licenseExpires,
      notesHTML: data.notesHTML,
      isAtlasGame: data.isAtlasGame ?? false,
    };
    if (data.publisherCompanyId !== '') {
      gameRecord = {
        ...gameRecord,
        publisherCompanyId: data.publisherCompanyId,
      };
    }
    if (data.developerCompanyId !== '') {
      gameRecord = {
        ...gameRecord,
        developerCompanyId: data.developerCompanyId,
      };
    }

    if (data.gameFranchiseId !== '') {
      gameRecord = {
        ...gameRecord,
        gameFranchiseId: data.gameFranchiseId,
      };
    }

    dispatch(saveGame(gameRecord))
      .unwrap()
      .then((savedGame: any) => {
        toast.success(t('Game saved'));
        done(savedGame);
      })
      .catch((e: any) => {
        toast.error(e.message ?? t('Failed to save game'));
      });
  };

  useEffect(() => {
    if (id) {
      getGameById(id).then((d: any) => {
        setGame(d);
      });
    } else {
      setGame({ id: '' });
    }
  }, [id]);

  const [allowedPublishers, setAllowedPublishers] = useState<any>();

  useEffect(() => {
    if (!registerOnFranchiseCreated) return;
    registerOnFranchiseCreated((franchise: any) => {
      setGame((g: any) => (g ? { ...g, gameFranchiseId: franchise.id } : g));
      setValue('gameFranchiseId', franchise.id);
    });
    return () => registerOnFranchiseCreated(undefined);
  }, [registerOnFranchiseCreated, setValue]);

  useEffect(() => {
    let pubs = getCompanyIdsForPermission(
      currentUser,
      permissionTypes.MANAGE_GAMES
    );
    setAllowedPublishers(pubs);
  }, [companies, currentUser]);

  return (
    <SlideoutLayout
      title={`${id ? t('Edit game') : t('Add game')}`}
      cancel={cancel}
      bar={
        <Button
          type="submit"
          text={t('Save')}
          onClick={handleSubmit(onSubmit)}
          className="btn btn-primary"
          testId="save-game-button"
        />
      }
    >
      {companies && game && (
        <GameForm
          game={game}
          gameFranchises={gameFranchises}
          companies={companies}
          allowedPublishers={allowedPublishers}
          register={register}
          errors={errors}
          control={control}
          clearLicenseExpires={() => {
            setGame({ ...game, licenseExpires: null });
          }}
          addGameFranchise={addGameFranchise}
        />
      )}
    </SlideoutLayout>
  );
}

export default EditGame;
